/**
 * CSV export.
 *
 * Two things worth being careful about, both because every cell here is text a BD typed:
 *
 * 1. **Quoting.** A company name with a comma, a quote, or a newline in it would otherwise
 *    shift every later column on that row. Fields are quoted whenever they contain any of
 *    those, and embedded quotes are doubled, per RFC 4180.
 *
 * 2. **Formula injection.** Excel and Sheets evaluate a cell beginning `=`, `+`, `-`, `@`, or a
 *    tab/CR as a formula. A guest name of `=HYPERLINK(...)` would run when a marketer opens the
 *    file. Such values are prefixed with a single quote, which spreadsheets strip on display
 *    but do not execute. This is the reason this module exists rather than a one-line join.
 *
 * The download itself is separate (see {@link downloadCsv}) so the formatting is testable
 * without a DOM.
 */

/** Characters a spreadsheet treats as starting a formula. */
const FORMULA_START = /^[=+\-@\t\r]/;

/** One field, escaped for CSV and made inert for spreadsheets. */
export function escapeField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  // Prefixed BEFORE quoting, so the guard ends up inside the quotes rather than outside.
  const safe = FORMULA_START.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * A CSV document from a header row and body rows.
 *
 * CRLF line endings, which is what RFC 4180 specifies and what Excel on Windows expects; a
 * bare LF makes some versions read the whole file as one row.
 */
export function toCsv(
  headers: readonly string[],
  rows: ReadonlyArray<readonly unknown[]>,
): string {
  return [headers, ...rows].map((row) => row.map(escapeField).join(',')).join('\r\n');
}

/**
 * Offers a CSV as a file download.
 *
 * A BOM leads the content: without it Excel reads UTF-8 as the system code page, and every
 * Chinese column header and company name in this export arrives as mojibake.
 *
 * Separated from {@link toCsv} because it is all side effect — it creates an object URL, clicks
 * a link, and revokes the URL. Revoking matters: each un-revoked URL pins its blob in memory
 * for the lifetime of the document.
 */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** A filename-safe version of a name, for `<event> attendance.csv`. */
export function csvFilename(base: string, fallback = 'export'): string {
  const cleaned = base
    .trim()
    // Anything a filesystem or a Content-Disposition header would argue about.
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, '-');
  return `${cleaned || fallback}.csv`;
}
